/**
 * WegfahrerDetail.js – Admin: Phase 2 Vervollständigung + PDF-Download
 * Pflichtfelder: Kennzeichen (oder "nicht erkennbar"), Fahrzeug (oder "nicht erkennbar"), mind. 1 Foto
 */
import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Download, Save, Camera, X, AlertTriangle,
  CheckCircle2, Loader2, Fuel, EyeOff, Video, Trash2, ExternalLink, Image as ImageIcon,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_FLOW = [
  { key: 'neu',            label: 'Neu',                  next: 'in_bearbeitung',  nextLabel: '→ In Bearbeitung' },
  { key: 'in_bearbeitung', label: 'In Bearbeitung',        next: 'an_anwalt',       nextLabel: '→ An Anwalt' },
  { key: 'an_anwalt',      label: 'An Anwalt gesendet',    next: 'wurde_bezahlt',   nextLabel: '→ Wurde bezahlt' },
  { key: 'wurde_bezahlt',  label: 'Wurde bezahlt',          next: null,              nextLabel: null },
];

const STATUS_CLS = {
  neu:             'bg-yellow-100 text-yellow-800',
  in_bearbeitung:  'bg-blue-100 text-blue-800',
  an_anwalt:       'bg-purple-100 text-purple-800',
  wurde_bezahlt:   'bg-green-100 text-green-800',
};

const CASE_TYPES = ['Vorsatz-Wegfahrer', 'Säulenverwechslung', 'Schuldanerkenntnis', 'Sonstiges'];

const COUNTRIES = [
  { code: 'D', name: 'Deutschland (D)' },
  { code: 'A', name: 'A – Österreich' }, { code: 'AL', name: 'AL – Albanien' },
  { code: 'AND', name: 'AND – Andorra' }, { code: 'B', name: 'B – Belgien' },
  { code: 'BG', name: 'BG – Bulgarien' }, { code: 'BIH', name: 'BIH – Bosnien' },
  { code: 'BY', name: 'BY – Weißrussland' }, { code: 'CH', name: 'CH – Schweiz' },
  { code: 'CY', name: 'CY – Zypern' }, { code: 'CZ', name: 'CZ – Tschechien' },
  { code: 'DK', name: 'DK – Dänemark' }, { code: 'E', name: 'E – Spanien' },
  { code: 'EST', name: 'EST – Estland' }, { code: 'F', name: 'F – Frankreich' },
  { code: 'FIN', name: 'FIN – Finnland' }, { code: 'FL', name: 'FL – Liechtenstein' },
  { code: 'GB', name: 'GB – Großbritannien' }, { code: 'GR', name: 'GR – Griechenland' },
  { code: 'H', name: 'H – Ungarn' }, { code: 'HR', name: 'HR – Kroatien' },
  { code: 'I', name: 'I – Italien' }, { code: 'IRL', name: 'IRL – Irland' },
  { code: 'IS', name: 'IS – Island' }, { code: 'L', name: 'L – Luxemburg' },
  { code: 'LT', name: 'LT – Litauen' }, { code: 'LV', name: 'LV – Lettland' },
  { code: 'M', name: 'M – Malta' }, { code: 'MC', name: 'MC – Monaco' },
  { code: 'MD', name: 'MD – Moldau' }, { code: 'ME', name: 'ME – Montenegro' },
  { code: 'MK', name: 'MK – Nordmazedonien' }, { code: 'N', name: 'N – Norwegen' },
  { code: 'NL', name: 'NL – Niederlande' }, { code: 'P', name: 'P – Portugal' },
  { code: 'PL', name: 'PL – Polen' }, { code: 'RO', name: 'RO – Rumänien' },
  { code: 'RSM', name: 'RSM – San Marino' }, { code: 'RUS', name: 'RUS – Russland' },
  { code: 'S', name: 'S – Schweden' }, { code: 'SK', name: 'SK – Slowakei' },
  { code: 'SLO', name: 'SLO – Slowenien' }, { code: 'SRB', name: 'SRB – Serbien' },
  { code: 'TR', name: 'TR – Türkei' }, { code: 'UA', name: 'UA – Ukraine' },
  { code: 'V', name: 'V – Vatikan' },
  { code: '_other', name: 'Sonstige (Freitext)' },
];

const NOT_RECOGNIZABLE = 'Nicht erkennbar';

export const WegfahrerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const receiptRef = useRef(null);       // Kamera
  const receiptGalleryRef = useRef(null); // Galerie
  const vehicleRef = useRef(null);
  const vehicleGalleryRef = useRef(null);

  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  const [form, setForm] = useState({
    license_plate: '', country_code: 'D', car_make_model: '',
    case_type: '', external_case_nr: '', notes: '',
    evidence_on_external_storage: false,
  });
  const [customCountry, setCustomCountry] = useState('');
  const [plateNotRecognizable, setPlateNotRecognizable] = useState(false);
  const [vehicleNotRecognizable, setVehicleNotRecognizable] = useState(false);
  const [receiptPhoto, setReceiptPhoto] = useState(null);
  const [vehiclePhoto, setVehiclePhoto] = useState(null);
  const [extraPhoto1, setExtraPhoto1] = useState(null);
  const [extraPhoto2, setExtraPhoto2] = useState(null);
  const extraPhoto1Ref = useRef(null);
  const extraPhoto1GalleryRef = useRef(null);
  const extraPhoto2Ref = useRef(null);
  const extraPhoto2GalleryRef = useRef(null);

  // Lightbox
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [videoUploading, setVideoUploading] = useState({ 1: false, 2: false, 3: false, 4: false, 5: false, 6: false });
  const videoGalleryRefs = { 1: useRef(null), 2: useRef(null), 3: useRef(null), 4: useRef(null), 5: useRef(null), 6: useRef(null) };

  // Wiederverwendbarer Bestätigungs-Dialog für unwiderrufliche Löschungen
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, items, onConfirm, confirmLabel, loading }

  const loadCase = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/api/wegfahrer/cases/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setCaseData(res.data);
      const lp = res.data.license_plate || '';
      const vm = res.data.car_make_model || '';
      setPlateNotRecognizable(lp === NOT_RECOGNIZABLE);
      setVehicleNotRecognizable(vm === NOT_RECOGNIZABLE);
      setForm({
        license_plate: lp === NOT_RECOGNIZABLE ? '' : lp,
        country_code: res.data.country_code || 'D',
        car_make_model: vm === NOT_RECOGNIZABLE ? '' : vm,
        case_type: res.data.case_type || '',
        external_case_nr: res.data.external_case_nr || '',
        notes: res.data.notes || '',
        evidence_on_external_storage: res.data.evidence_on_external_storage || false,
      });
    } catch { toast.error('Fall nicht gefunden'); navigate('/admin/wegfahrer'); }
    finally { setLoading(false); }
  };

  // Foto/Video herunterladen
  const downloadFile = (src, filename) => {
    const a = document.createElement('a');
    a.href = src;
    a.download = filename;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // eslint-disable-next-line
  useEffect(() => { loadCase(); }, [id]);

  const compressPhoto = (file, cb) => {
    const reader = new FileReader();
    reader.onload = ev => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const max = 1400;
        const ratio = Math.min(max / img.width, max / img.height, 1);
        canvas.width = img.width * ratio; canvas.height = img.height * ratio;
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        cb(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    // ── Pflichtfeld-Validierung ───────────────────────────────────────────────
    const plateValue = plateNotRecognizable ? NOT_RECOGNIZABLE : form.license_plate.trim();
    const vehicleValue = vehicleNotRecognizable ? NOT_RECOGNIZABLE : form.car_make_model.trim();

    if (!plateValue) {
      toast.error('Kennzeichen ist Pflicht (oder "Nicht erkennbar" markieren)');
      return;
    }
    if (!vehicleValue) {
      toast.error('Fahrzeug ist Pflicht (oder "Nicht erkennbar" markieren)');
      return;
    }
    const hasExistingReceipt = caseData?.receipt_photo_b64 && !caseData?.photos_deleted_at;
    const hasPhoto = receiptPhoto || hasExistingReceipt;
    if (!hasPhoto) {
      toast.error('Kassenbon-Foto ist Pflicht');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = { ...form, license_plate: plateValue, car_make_model: vehicleValue };
      if (form.country_code === '_other') payload.country_code = customCountry.toUpperCase() || 'Sonstige';
      if (receiptPhoto) payload.receipt_photo_b64 = receiptPhoto;
      if (vehiclePhoto) payload.vehicle_photo_b64 = vehiclePhoto;
      if (extraPhoto1) payload.extra_photo_1_b64 = extraPhoto1;
      if (extraPhoto2) payload.extra_photo_2_b64 = extraPhoto2;
      await axios.put(`${API}/api/wegfahrer/cases/${id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Fall gespeichert');
      await loadCase();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  const handleStatus = async (newStatus) => {
    setStatusLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/wegfahrer/cases/${id}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Status aktualisiert');
      await loadCase();
    } catch { toast.error('Fehler'); }
    finally { setStatusLoading(false); }
  };

  // Liste der aktuell vorhandenen Medien (für Warn-Dialog)
  const getMediaList = () => {
    const list = [];
    if (caseData?.receipt_photo_b64) list.push('Kassenbon-Foto');
    if (caseData?.vehicle_photo_b64) list.push('Fahrzeug-Foto');
    if (caseData?.extra_photo_1_b64) list.push('Beweisfoto 1');
    if (caseData?.extra_photo_2_b64) list.push('Beweisfoto 2');
    [1, 2, 3, 4, 5, 6].forEach(s => { if (caseData?.[`video_url_${s}`]) list.push(`Video ${s}`); });
    return list;
  };

  const handleDeleteMedia = () => {
    setConfirmDialog({
      title: 'Fotos & Videos endgültig löschen?',
      items: getMediaList(),
      confirmLabel: 'Ja, alle Medien löschen',
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, loading: true }));
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${API}/api/wegfahrer/cases/${id}/media`, { headers: { Authorization: `Bearer ${token}` } });
          toast.success('Fotos & Videos gelöscht');
          setConfirmDialog(null);
          await loadCase();
        } catch { toast.error('Fehler beim Löschen'); setConfirmDialog(d => ({ ...d, loading: false })); }
      },
    });
  };

  const getVideoDuration = (file) => new Promise((resolve) => {
    try {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.onloadedmetadata = () => { try { URL.revokeObjectURL(v.src); } catch (_) {} resolve(v.duration); };
      v.onerror = () => { try { URL.revokeObjectURL(v.src); } catch (_) {} resolve(null); };
      v.src = URL.createObjectURL(file);
    } catch (_) { resolve(null); }
  });

  const handleVideoUpload = async (slot, file) => {
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) { toast.error('Video zu groß (max. 200MB)'); return; }

    // Max. 1 Minute pro Video (clientseitig geprüft). Toleranz bis 65s.
    const duration = await getVideoDuration(file);
    if (duration && duration > 65) {
      toast.error(`Video zu lang (${Math.round(duration)} Sek.). Maximal 1 Minute pro Video erlaubt.`, { duration: 7000 });
      return;
    }

    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    if (file.size > 30 * 1024 * 1024) {
      toast.info(`Großes Video (${sizeMB} MB) – bitte warten, Upload kann 2-5 Min. dauern`, { duration: 6000 });
    }

    setVideoUploading(v => ({ ...v, [slot]: true }));
    const token = localStorage.getItem('token');
    const videoMime = file.type || 'video/mp4';

    const extractErr = (e) => {
      const detail = e.response?.data?.detail;
      if (typeof detail === 'string' && detail) return detail;
      if (Array.isArray(detail) && detail.length > 0) return detail.map(d => d.msg || JSON.stringify(d)).join('; ');
      return e.message || `Video ${slot} fehlgeschlagen`;
    };

    try {
      if (file.size <= 10 * 1024 * 1024) {
        // Kleines Video (< 10MB): Original-File direkt
        toast.loading(`Video ${slot} wird hochgeladen...`, { id: `vid-${slot}` });
        const form = new FormData();
        form.append('file', file, file.name);
        await axios.post(
          `${API}/api/wegfahrer/cases/${id}/video/${slot}/chunk?chunk_index=0&total_chunks=1&filename=${encodeURIComponent(file.name)}`,
          form,
          { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
        );
      } else {
        // Großes Video (≥ 10MB): ArrayBuffer lesen → Chunks
        toast.loading(`Video ${slot} wird vorbereitet...`, { id: `vid-${slot}` });
        let buffer;
        if (typeof file.arrayBuffer === 'function') {
          buffer = await file.arrayBuffer();
        } else {
          buffer = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload  = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsArrayBuffer(file);
          });
        }
        const CHUNK_SIZE = 512 * 1024;
        const totalChunks = Math.ceil(buffer.byteLength / CHUNK_SIZE);
        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end   = Math.min(start + CHUNK_SIZE, buffer.byteLength);
          const chunkBlob = new Blob([buffer.slice(start, end)], { type: videoMime });
          const chunkForm = new FormData();
          chunkForm.append('file', chunkBlob, file.name);
          const pct = Math.round(((i + 1) / totalChunks) * 90);
          toast.loading(
            totalChunks > 10
              ? `Video ${slot}: Teil ${i+1}/${totalChunks} (${pct}%) – bitte warten...`
              : `Video ${slot} wird hochgeladen... ${pct}%`,
            { id: `vid-${slot}` }
          );
          let uploaded = false;
          for (let attempt = 0; attempt < 3; attempt++) {
            try {
              await axios.post(
                `${API}/api/wegfahrer/cases/${id}/video/${slot}/chunk?chunk_index=${i}&total_chunks=${totalChunks}&filename=${encodeURIComponent(file.name)}`,
                chunkForm,
                { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
              );
              uploaded = true;
              break;
            } catch (chunkErr) {
              if (attempt === 2) throw chunkErr;
              await new Promise(r => setTimeout(r, 2000));
            }
          }
          if (!uploaded) throw new Error(`Upload-Teil ${i+1} fehlgeschlagen`);
        }
      }

      toast.loading(`Video ${slot} wird in Cloud gespeichert...`, { id: `vid-${slot}` });
      await axios.post(
        `${API}/api/wegfahrer/cases/${id}/video/${slot}/finalize`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 600000 }
      );
      toast.success(`Video ${slot} hochgeladen! (${sizeMB} MB)`, { id: `vid-${slot}` });
      await loadCase();
    } catch (e) {
      const errMsg = extractErr(e);
      console.error('[Wegfahrer Video Upload]', e.response?.status, e.response?.data, e.message);
      toast.error(`${errMsg}. Tipp: Kürzere Videos laden zuverlässiger.`, { id: `vid-${slot}`, duration: 7000 });
    } finally {
      setVideoUploading(v => ({ ...v, [slot]: false }));
    }
  };


  const handleVideoDelete = (slot) => {
    setConfirmDialog({
      title: `Video ${slot} endgültig löschen?`,
      items: [`Video ${slot}`],
      confirmLabel: 'Ja, Video löschen',
      onConfirm: async () => {
        setConfirmDialog(d => ({ ...d, loading: true }));
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${API}/api/wegfahrer/cases/${id}/video/${slot}`, { headers: { Authorization: `Bearer ${token}` } });
          toast.success(`Video ${slot} gelöscht`);
          setConfirmDialog(null);
          await loadCase();
        } catch { toast.error('Fehler beim Löschen'); setConfirmDialog(d => ({ ...d, loading: false })); }
      },
    });
  };

  const handlePdf = async () => {
    setPdfLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/wegfahrer/cases/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url;
      a.download = `${caseData?.case_nr || 'wegfahrer'}.pdf`; a.click();
      toast.success('PDF heruntergeladen');
    } catch { toast.error('PDF-Fehler'); }
    finally { setPdfLoading(false); }
  };

  if (loading) return (
    <DashboardLayout>
      <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin" /></div>
    </DashboardLayout>
  );

  const statusCfg = STATUS_FLOW.find(s => s.key === caseData?.status) || STATUS_FLOW[0];

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto p-3 sm:p-4 md:p-6 space-y-4 pb-8">
        {/* Header */}
        <div className="flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate('/admin/wegfahrer')}
            className="p-2 hover:bg-muted rounded-xl shrink-0 touch-manipulation">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-xl font-bold flex items-center gap-2 truncate">
              <Fuel className="h-4 w-4 sm:h-5 sm:w-5 text-red-600 shrink-0" />
              {caseData?.case_nr}
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground truncate">
              {caseData?.incident_date} {caseData?.incident_time} Uhr · Säule {caseData?.pump_nr} · {caseData?.fuel_type}
            </p>
          </div>
          <Button onClick={handlePdf} disabled={pdfLoading} variant="outline" size="sm"
            className="gap-1.5 shrink-0 touch-manipulation">
            {pdfLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            <span className="hidden sm:inline">PDF</span>
          </Button>
        </div>

        {/* Status */}
        <Card>
          <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_CLS[caseData?.status] || ''}`}>
              {statusCfg.label}
            </span>
            {statusCfg.next && (
              <Button onClick={() => handleStatus(statusCfg.next)} disabled={statusLoading}
                variant={statusCfg.next === 'wurde_bezahlt' ? 'destructive' : 'default'} size="sm" className="gap-1.5"
                data-testid="status-next-btn">
                {statusLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                {statusCfg.nextLabel}
              </Button>
            )}
            {caseData?.status === 'wurde_bezahlt' && !caseData?.photos_deleted_at && (
              <div className="flex items-center gap-1.5 text-green-600 text-sm">
                <CheckCircle2 className="h-4 w-4" /> Abgeschlossen
              </div>
            )}
            {caseData?.photos_deleted_at && (
              <div className="flex items-center gap-1.5 text-amber-600 text-sm">
                <AlertTriangle className="h-4 w-4" /> Medien gelöscht
              </div>
            )}
          </CardContent>
        </Card>

        {/* Separate, bewusste Datenschutz-Löschung (entkoppelt vom Status) */}
        {!caseData?.photos_deleted_at && getMediaList().length > 0 && (
          <Card className="border-red-200">
            <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="text-sm font-medium flex items-center gap-1.5">
                  <Trash2 className="h-4 w-4 text-red-500 shrink-0" /> Fotos & Videos löschen
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Entfernt alle {getMediaList().length} Medien dieses Falls unwiderruflich (Datenschutz).
                </p>
              </div>
              <Button onClick={handleDeleteMedia} variant="destructive" size="sm" className="gap-1.5 shrink-0"
                data-testid="delete-media-btn">
                <Trash2 className="h-4 w-4" /> Fotos & Videos jetzt löschen
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Phase 1 (schreibgeschützt) – JETZT MIT KRAFTSTOFF */}
        <Card>
          <CardContent className="p-4 space-y-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase mb-3">Phase 1 – Mitarbeiter-Meldung</p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
              {[
                ['Kraftstoff', caseData?.fuel_type],
                ['Zapfsäule', `Säule ${caseData?.pump_nr}`],
                ['Betrag', `${caseData?.amount?.toFixed(2)} €`],
                ['Literpreis', `${caseData?.price_per_liter?.toFixed(3)} €/L`],
                ['Liter', `${caseData?.liters?.toFixed(2)} L`],
                ['Gemeldet von', caseData?.created_by_name],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b py-1">
                  <span className="text-muted-foreground">{k}</span>
                  <span className="font-medium">{v || '–'}</span>
                </div>
              ))}
            </div>
            {caseData?.receipt_photo_b64 && !caseData?.photos_deleted_at && (
              <div className="mt-3">
                <p className="text-xs text-muted-foreground mb-1">Kassenbon-Foto</p>
                <div className="relative inline-block">
                  <img
                    src={caseData.receipt_photo_b64}
                    alt="Kassenbon"
                    className="max-h-40 rounded-lg border object-contain cursor-zoom-in hover:opacity-90 transition-opacity"
                    onClick={() => setLightboxSrc(caseData.receipt_photo_b64)}
                    title="Klicken zum Vergrößern"
                  />
                  <button
                    type="button"
                    onClick={() => downloadFile(caseData.receipt_photo_b64, `${caseData.case_nr || 'wegfahrer'}_kassenbon.jpg`)}
                    className="absolute top-1 left-1 p-1.5 bg-black/60 hover:bg-black/80 text-white rounded-lg transition-colors"
                    title="Foto herunterladen"
                  >
                    <Download className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )}
            {caseData?.photos_deleted_at && (
              <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 p-2 rounded-lg mt-2">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                Fotos gelöscht (Datenschutz)
              </div>
            )}
          </CardContent>
        </Card>

        {/* Phase 2 – Admin-Ergänzung */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase">Phase 2 – Admin-Ergänzung</p>

            {/* Kennzeichen + Nicht erkennbar – auf Mobile untereinander */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Land</Label>
                <select value={form.country_code} onChange={e => setForm(f => ({...f, country_code: e.target.value}))}
                  disabled={plateNotRecognizable}
                  className="w-full h-9 px-3 rounded-lg border bg-background text-sm disabled:opacity-50">
                  {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>Kennzeichen <span className="text-red-500">*</span></Label>
                  <button
                    type="button"
                    onClick={() => { setPlateNotRecognizable(v => !v); setForm(f => ({...f, license_plate: ''})); }}
                    className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border transition-all ${
                      plateNotRecognizable
                        ? 'bg-amber-100 text-amber-700 border-amber-300 font-medium'
                        : 'text-muted-foreground border-border hover:border-amber-300'
                    }`}
                    title="Kennzeichen nicht erkennbar">
                    <EyeOff className="h-3 w-3" />
                    Nicht erkennbar
                  </button>
                </div>
                {plateNotRecognizable ? (
                  <div className="h-9 px-3 flex items-center rounded-lg border bg-amber-50 text-amber-700 text-sm font-medium">
                    Nicht erkennbar
                  </div>
                ) : form.country_code === '_other' ? (
                  <div className="flex gap-1">
                    <Input placeholder="KZ" value={customCountry} onChange={e => setCustomCountry(e.target.value.toUpperCase())} className="w-16" />
                    <Input placeholder="S-AB 1234" value={form.license_plate}
                      onChange={e => setForm(f => ({...f, license_plate: e.target.value.toUpperCase()}))} />
                  </div>
                ) : (
                  <Input placeholder="S-AB 1234" value={form.license_plate}
                    onChange={e => setForm(f => ({...f, license_plate: e.target.value.toUpperCase()}))} />
                )}
              </div>
            </div>

            {/* Fahrzeug + Nicht erkennbar */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>Fahrzeug (Marke, Modell, Farbe) <span className="text-red-500">*</span></Label>
                <button
                  type="button"
                  onClick={() => { setVehicleNotRecognizable(v => !v); setForm(f => ({...f, car_make_model: ''})); }}
                  className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border transition-all ${
                    vehicleNotRecognizable
                      ? 'bg-amber-100 text-amber-700 border-amber-300 font-medium'
                      : 'text-muted-foreground border-border hover:border-amber-300'
                  }`}
                  title="Fahrzeug nicht erkennbar">
                  <EyeOff className="h-3 w-3" />
                  Nicht erkennbar
                </button>
              </div>
              {vehicleNotRecognizable ? (
                <div className="h-9 px-3 flex items-center rounded-lg border bg-amber-50 text-amber-700 text-sm font-medium">
                  Nicht erkennbar
                </div>
              ) : (
                <Input placeholder="z.B. VW Golf, weiß" value={form.car_make_model}
                  onChange={e => setForm(f => ({...f, car_make_model: e.target.value}))} />
              )}
            </div>

            {/* Falltyp + Externe Fallnr. – auf Mobile untereinander */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Falltyp</Label>
                <select value={form.case_type} onChange={e => setForm(f => ({...f, case_type: e.target.value}))}
                  className="w-full h-9 px-3 rounded-lg border bg-background text-sm">
                  <option value="">Typ wählen</option>
                  {CASE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Externe Fallnr. (optional)</Label>
                <Input placeholder="z.B. ANW-2026-123" value={form.external_case_nr}
                  onChange={e => setForm(f => ({...f, external_case_nr: e.target.value}))} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Bemerkung (optional)</Label>
              <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))}
                rows={2} placeholder="Zusätzliche Informationen..."
                className="w-full px-3 py-2 rounded-lg border bg-background text-sm resize-none" />
            </div>

            {/* Beweismaterial auf externem Datenträger */}
            <label className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl cursor-pointer hover:bg-blue-100 transition-colors">
              <input
                type="checkbox"
                checked={form.evidence_on_external_storage}
                onChange={e => setForm(f => ({...f, evidence_on_external_storage: e.target.checked}))}
                className="h-4 w-4 rounded border-blue-400 text-blue-600"
                data-testid="evidence-external-checkbox"
              />
              <div>
                <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                  Beweismaterial auf externem Datenträger gesichert
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  z.B. USB-Stick, Videokamera-SD, Cloud-Backup
                </p>
              </div>
            </label>

            {/* Fotos – Pflicht! */}
            {!caseData?.photos_deleted_at && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Fotos <span className="text-red-500">*</span></Label>
                  <span className="text-xs text-muted-foreground">Kassenbon-Foto Pflicht · Fahrzeugfoto optional</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { label: 'Beleg (Pflicht)', key: 'receipt', cameraRef: receiptRef, galleryRef: receiptGalleryRef, state: receiptPhoto, setState: setReceiptPhoto, existing: caseData?.receipt_photo_b64, required: true, isReceipt: true },
                    { label: 'Fahrzeugfoto (optional)', key: 'vehicle', cameraRef: vehicleRef, galleryRef: vehicleGalleryRef, state: vehiclePhoto, setState: setVehiclePhoto, existing: caseData?.vehicle_photo_b64, required: false, isReceipt: false },
                  ].map(({ label, key, cameraRef, galleryRef, state, setState, existing, required, isReceipt }) => (
                    <div key={key} className="space-y-1.5">
                      <Label className="text-xs">{label}</Label>
                      {(state || existing) ? (
                        <div className="relative">
                          <img
                            src={state || existing}
                            alt={label}
                            className="w-full h-28 object-cover rounded-lg border cursor-zoom-in hover:opacity-90 transition-opacity"
                            onClick={() => setLightboxSrc(state || existing)}
                            title="Klicken zum Vergrößern"
                          />
                          {/* Download */}
                          <button type="button"
                            onClick={() => downloadFile(state || existing, `${caseData?.case_nr || 'wegfahrer'}_${key}.jpg`)}
                            className="absolute top-1 left-1 p-1 bg-black/60 hover:bg-black/80 text-white rounded-lg transition-colors"
                            title="Herunterladen">
                            <Download className="h-3 w-3" />
                          </button>
                          {/* Löschen (nur wenn neu hochgeladen) */}
                          {state && (
                            <button type="button" onClick={() => setState(null)}
                              className="absolute top-1 right-8 p-0.5 bg-red-500 text-white rounded-full">
                              <X className="h-3 w-3" />
                            </button>
                          )}
                          {/* Beleg ersetzen: Kamera + Galerie */}
                          <div className="absolute bottom-1 right-1 flex gap-1">
                            <button type="button" onClick={() => galleryRef.current?.click()}
                              className="p-1.5 bg-white/90 hover:bg-white rounded-lg shadow text-xs flex items-center gap-0.5"
                              title={isReceipt ? "Beleg aus Galerie ersetzen" : "Foto aus Galerie"}>
                              <ImageIcon className="h-3 w-3 text-blue-600" />
                            </button>
                            <button type="button" onClick={() => cameraRef.current?.click()}
                              className="p-1.5 bg-white/90 hover:bg-white rounded-lg shadow text-xs flex items-center gap-0.5"
                              title={isReceipt ? "Beleg mit Kamera neu aufnehmen" : "Foto mit Kamera"}>
                              <Camera className="h-3 w-3 text-gray-700" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className={`w-full h-28 border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-2 ${
                          required ? 'border-red-200' : 'border-gray-200'
                        }`}>
                          <p className={`text-xs font-medium ${required ? 'text-red-500' : 'text-muted-foreground'}`}>
                            {required ? 'Pflicht – Beleg hinzufügen' : 'Optional'}
                          </p>
                          <div className="flex gap-2">
                            <button type="button" onClick={() => galleryRef.current?.click()}
                              className="flex items-center gap-1 text-xs px-2 py-1.5 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors">
                              <ImageIcon className="h-3.5 w-3.5" /> Galerie
                            </button>
                            <button type="button" onClick={() => cameraRef.current?.click()}
                              className="flex items-center gap-1 text-xs px-2 py-1.5 bg-muted/60 border border-border rounded-lg hover:bg-muted transition-colors">
                              <Camera className="h-3.5 w-3.5" /> Kamera
                            </button>
                          </div>
                        </div>
                      )}
                      {/* Kamera-Input */}
                      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
                        onChange={e => e.target.files?.[0] && compressPhoto(e.target.files[0], setState)} />
                      {/* Galerie-Input (kein capture → zeigt Galerie) */}
                      <input ref={galleryRef} type="file" accept="image/*" className="hidden"
                        onChange={e => e.target.files?.[0] && compressPhoto(e.target.files[0], setState)} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 2 Optionale Zusatz-Beweisfotos */}
            {!caseData?.photos_deleted_at && (
              <div className="space-y-2">
                <Label className="text-muted-foreground text-xs">
                  Zusätzliche Beweisfotos (optional)
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { label: 'Beweisfoto 1', cameraRef: extraPhoto1Ref, galleryRef: extraPhoto1GalleryRef, state: extraPhoto1, setState: setExtraPhoto1, existing: caseData?.extra_photo_1_b64 },
                    { label: 'Beweisfoto 2', cameraRef: extraPhoto2Ref, galleryRef: extraPhoto2GalleryRef, state: extraPhoto2, setState: setExtraPhoto2, existing: caseData?.extra_photo_2_b64 },
                  ].map(({ label, cameraRef, galleryRef, state, setState, existing }) => (
                    <div key={label} className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">{label}</Label>
                      {(state || existing) ? (
                        <div className="relative">
                          <img
                            src={state || existing}
                            alt={label}
                            className="w-full h-24 object-cover rounded-lg border cursor-zoom-in hover:opacity-90 transition-opacity"
                            onClick={() => setLightboxSrc(state || existing)}
                            title="Klicken zum Vergrößern"
                          />
                          {/* Download */}
                          <button type="button"
                            onClick={() => downloadFile(state || existing, `${caseData?.case_nr || 'wegfahrer'}_${label.replace(' ', '_').toLowerCase()}.jpg`)}
                            className="absolute top-1 left-1 p-1 bg-black/60 hover:bg-black/80 text-white rounded-lg transition-colors"
                            title="Herunterladen">
                            <Download className="h-3 w-3" />
                          </button>
                          {state && (
                            <button type="button" onClick={() => setState(null)}
                              className="absolute top-1 right-1 p-0.5 bg-red-500 text-white rounded-full">
                              <X className="h-3 w-3" />
                            </button>
                          )}
                          {/* Ersetzen: Galerie + Kamera */}
                          <div className="absolute bottom-1 right-1 flex gap-1">
                            <button type="button" onClick={() => galleryRef.current?.click()}
                              className="p-1.5 bg-white/90 hover:bg-white rounded-lg shadow" title="Galerie">
                              <ImageIcon className="h-3 w-3 text-blue-600" />
                            </button>
                            <button type="button" onClick={() => cameraRef.current?.click()}
                              className="p-1.5 bg-white/90 hover:bg-white rounded-lg shadow" title="Kamera">
                              <Camera className="h-3 w-3 text-gray-700" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="w-full h-24 border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center gap-2">
                          <p className="text-xs text-muted-foreground">Optional</p>
                          <div className="flex gap-2">
                            <button type="button" onClick={() => galleryRef.current?.click()}
                              className="flex items-center gap-1 text-xs px-2 py-1 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors">
                              <ImageIcon className="h-3 w-3" /> Galerie
                            </button>
                            <button type="button" onClick={() => cameraRef.current?.click()}
                              className="flex items-center gap-1 text-xs px-2 py-1 bg-muted/60 border border-border rounded-lg hover:bg-muted transition-colors">
                              <Camera className="h-3 w-3" /> Kamera
                            </button>
                          </div>
                        </div>
                      )}
                      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
                        onChange={e => e.target.files?.[0] && compressPhoto(e.target.files[0], setState)} />
                      <input ref={galleryRef} type="file" accept="image/*" className="hidden"
                        onChange={e => e.target.files?.[0] && compressPhoto(e.target.files[0], setState)} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button onClick={handleSave} disabled={saving} className="w-full gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Speichern
            </Button>

            {/* ── VIDEO-BEWEISMITTEL (optional, Admin/SL) ────────────────── */}
            {!caseData?.photos_deleted_at && (
              <div className="border-t pt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Video className="h-4 w-4 text-indigo-600" />
                    <Label className="text-sm font-semibold">Video-Beweismittel</Label>
                    <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-500 px-1.5 py-0.5 rounded">Optional · max. 6 Videos · max. 1 Min.</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Videos werden sicher in der Cloud gespeichert. Das PDF enthält einen Link + QR-Code für den Anwalt.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[1, 2, 3, 4, 5, 6].map(slot => {
                    const existingUrl = caseData?.[`video_url_${slot}`];
                    const isUploading = videoUploading[slot];
                    return (
                      <div key={slot} className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Video {slot}</Label>
                        {existingUrl ? (
                          <div className="border-2 border-indigo-200 bg-indigo-50 dark:bg-indigo-950/20 rounded-xl p-3 space-y-2">
                            <div className="flex items-center gap-2">
                              <CheckCircle2 className="h-4 w-4 text-indigo-600 shrink-0" />
                              <span className="text-xs text-indigo-700 dark:text-indigo-400 font-medium">Video hochgeladen</span>
                            </div>
                            <div className="flex gap-1.5">
                              <a href={existingUrl} target="_blank" rel="noopener noreferrer"
                                className="flex-1 flex items-center justify-center gap-1 text-xs bg-indigo-600 text-white px-2 py-1.5 rounded-lg hover:bg-indigo-700 transition-colors">
                                <ExternalLink className="h-3 w-3" /> Ansehen
                              </a>
                              <button
                                onClick={() => downloadFile(existingUrl, `${caseData?.case_nr || 'wegfahrer'}_video${slot}.mp4`)}
                                className="flex items-center justify-center p-1.5 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-100 rounded-lg transition-colors"
                                title="Video herunterladen">
                                <Download className="h-3.5 w-3.5" />
                              </button>
                              <button onClick={() => handleVideoDelete(slot)}
                                className="flex items-center justify-center p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        ) : isUploading ? (
                          <div className="border-2 border-indigo-200 rounded-xl h-24 flex flex-col items-center justify-center gap-1">
                            <Loader2 className="h-5 w-5 text-indigo-400 animate-spin" />
                            <span className="text-xs text-indigo-500">Wird hochgeladen...</span>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {/* Hinweis: Kamera-Direktaufnahme nicht unterstützt */}
                            <p className="text-xs text-muted-foreground bg-muted/40 rounded-xl px-3 py-2 leading-relaxed">
                              Bitte nimm das Video zuerst mit der Kamera-App deines Geräts auf und wähle es anschließend hier aus der Galerie aus.
                            </p>
                            {/* Aus Galerie wählen */}
                            <button
                              type="button"
                              onClick={() => videoGalleryRefs[slot].current?.click()}
                              className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-indigo-200 rounded-xl hover:bg-indigo-50/40 dark:hover:bg-indigo-950/20 transition-colors"
                              data-testid={`video-gallery-${slot}`}
                            >
                              <svg className="h-4 w-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                              <span className="text-xs text-indigo-500 font-medium">Aus Galerie wählen</span>
                            </button>
                          </div>
                        )}
                        {/* Galerie-Input */}
                        <input
                          ref={videoGalleryRefs[slot]}
                          type="file"
                          accept="video/*"
                          className="hidden"
                          onChange={e => e.target.files?.[0] && handleVideoUpload(slot, e.target.files[0])}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Lightbox (iOS-safe: Buttons in Bottom Bar, nicht oben) ──────────── */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-50 bg-black flex flex-col"
          style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          {/* Bild – nimmt gesamten Raum ein außer Bottom Bar */}
          <div
            className="flex-1 flex items-center justify-center p-2 overflow-hidden"
            onClick={() => setLightboxSrc(null)}
          >
            <img
              src={lightboxSrc}
              alt="Vollansicht"
              className="max-w-full max-h-full object-contain"
              onClick={e => e.stopPropagation()}
            />
          </div>

          {/* Bottom Action Bar – immer erreichbar, nie unter iOS-UI */}
          <div
            className="shrink-0 bg-black/80 backdrop-blur-sm border-t border-white/10"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 gap-3">
              {/* Download */}
              <button
                onClick={() => downloadFile(lightboxSrc, `${caseData?.case_nr || 'wegfahrer'}_foto.jpg`)}
                className="flex items-center gap-2 px-5 py-3 bg-white/10 hover:bg-white/20 active:bg-white/30 text-white rounded-2xl transition-colors text-sm font-medium"
                data-testid="lightbox-download"
              >
                <Download className="h-5 w-5" />
                Herunterladen
              </button>

              {/* Tippen-Hinweis in der Mitte */}
              <span className="text-white/40 text-xs text-center hidden sm:block">
                Außerhalb tippen zum Schließen
              </span>

              {/* Schließen */}
              <button
                onClick={() => setLightboxSrc(null)}
                className="flex items-center gap-2 px-5 py-3 bg-white/10 hover:bg-white/20 active:bg-white/30 text-white rounded-2xl transition-colors text-sm font-medium"
                data-testid="lightbox-close"
              >
                <X className="h-5 w-5" />
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bestätigungs-Dialog für unwiderrufliche Löschungen */}
      {confirmDialog && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
          onClick={() => !confirmDialog.loading && setConfirmDialog(null)}>
          <div className="bg-background rounded-2xl shadow-xl max-w-md w-full p-5 space-y-4"
            onClick={e => e.stopPropagation()} data-testid="confirm-delete-dialog">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-full shrink-0">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div className="min-w-0">
                <h3 className="font-bold text-base">{confirmDialog.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Bist du sicher, dass dieser Vorgang durchgeführt werden soll?
                  Folgende Dateien werden dabei <span className="font-semibold text-red-600">unwiderruflich gelöscht</span> und können <span className="font-semibold">nicht wiederhergestellt</span> werden:
                </p>
              </div>
            </div>
            {confirmDialog.items?.length > 0 ? (
              <ul className="text-sm bg-red-50 border border-red-100 rounded-xl px-4 py-3 space-y-1">
                {confirmDialog.items.map((it, i) => (
                  <li key={i} className="flex items-center gap-2 text-red-800">
                    <X className="h-3.5 w-3.5 shrink-0" /> {it}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground bg-muted/40 rounded-xl px-4 py-3">
                Keine Medien vorhanden.
              </p>
            )}
            <div className="flex gap-2 justify-end pt-1">
              <Button variant="outline" size="sm" disabled={confirmDialog.loading}
                onClick={() => setConfirmDialog(null)} data-testid="confirm-cancel-btn">
                Abbrechen
              </Button>
              <Button variant="destructive" size="sm" disabled={confirmDialog.loading}
                onClick={confirmDialog.onConfirm} className="gap-1.5" data-testid="confirm-delete-btn">
                {confirmDialog.loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {confirmDialog.confirmLabel}
              </Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default WegfahrerDetail;
